import React, { useState, useRef, useEffect } from 'react';
import CharacterCard, { Character, CharacterCardRef } from './CharacterCard';
import { Link } from 'react-router-dom';
import { Skeleton } from './ui/skeleton';
import { isVideoUrl } from '@/lib/utils';

interface CharacterGridProps {
  characters: Character[];
  onCharacterClick?: (character: Character) => void;
  loading?: boolean;
}

const SKELETON_COUNT = 16;

function isTouchOnlyDeviceOrSmallScreen() {
  if (typeof window === 'undefined') return false;
  const isSmall = window.innerWidth < 640;
  const noHover = window.matchMedia('(any-hover: none)').matches;
  // if (import.meta.env.DEV) console.log('[DEV] isTouchOnlyDeviceOrSmallScreen', isSmall || noHover);
  return isSmall || noHover;
}

const INTERSECTION_THRESHOLD = 0.2;

const CharacterGrid: React.FC<CharacterGridProps> = ({ characters, onCharacterClick, loading }) => {
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const cardRefs = useRef<(CharacterCardRef | null)[]>([]);

  const charactersWithVideo = characters
    .filter(c => c.profile_media_fpaths.some(isVideoUrl))
    // Take first 3 characters only for performance reasons (page autorefresh after observer crash on mobile)
    .slice(0, 3);

  // IntersectionObserver logic for mobile/touch
  useEffect(() => {
    if (!isTouchOnlyDeviceOrSmallScreen() || !charactersWithVideo.length || !window.IntersectionObserver) return;
    let frame: number | null = null;
    let lastUpdate = 0;
    const observer = new window.IntersectionObserver(
      (entries) => {
        const now = Date.now();
        if (now - lastUpdate < 300) {
          if (frame) cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => observerCallback(entries));
          return;
        }
        observerCallback(entries);
        lastUpdate = now;
      },
      { threshold: [0, INTERSECTION_THRESHOLD] }
    );

    function observerCallback(entries: IntersectionObserverEntry[]) {
      // Map character id to intersectionRatio
      const ratios: Record<string, number> = {};
      let maxRatioId = '';
      let maxRatio = 0;
      entries.forEach((entry) => {
        const id = entry.target.getAttribute('data-char-id');
        // if (import.meta.env.DEV) {
        //   console.log(
        //     '[DEV] observerCallback',
        //     id,
        //     charactersWithVideo.find(c => c.id === id)?.url_slug, id ? entry.intersectionRatio : 'none'
        //   );
        // }
        if (id) ratios[id] = entry.intersectionRatio;
        if (entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          maxRatioId = id ?? '';
        }
      });

      Object.entries(ratios).forEach(([id, ratio]) => {
        const card = cardRefs.current[charactersWithVideo.findIndex(c => c.id === id)];
        const play = ratio > INTERSECTION_THRESHOLD;
        if (play && id === maxRatioId) card?.playImperative();
        else card?.stopImperative();
      });
    }
    // Observe card refs of characters with video
    // if (import.meta.env.DEV) console.log('[DEV] charactersWithVideo', charactersWithVideo);
    cardRefs.current.forEach((r) => {
      if (!r) return;
      const el = r.ref();
      if (el && charactersWithVideo.find(c => c.id === el.getAttribute('data-char-id'))) {
        observer.observe(el);
      }
    });
    return () => {
      if (import.meta.env.DEV) console.log('[DEV] disconnecting intersection observer');
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [charactersWithVideo]);

  if (loading) {
    return (
      <main className="p-4 grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-6">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <div key={i} className="relative bg-zinc-900 rounded-2xl shadow-lg overflow-hidden flex flex-col h-full group">
            <Skeleton className="w-full h-full min-h-72 aspect-[3/4]" />
            <div className="absolute bottom-0 left-0 w-full px-4 pb-4 pt-10 flex flex-col z-30">
              <Skeleton className="bg-zinc-500 h-7 w-2/3 mb-2" />
              <Skeleton className="bg-zinc-500 h-4 w-1/2 mb-2" />
              <div className="flex gap-2">
                <Skeleton className="bg-zinc-500 h-5 w-12 rounded-full" />
                <Skeleton className="bg-zinc-500 h-5 w-16 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </main>
    );
  }
  return (
    <main className="p-4 grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-6">
      {characters.map((char) => {
        const route = char.gender === 'm'
          ? `/ai-boyfriend/${char.url_slug}`
          : `/ai-girlfriend/${char.url_slug}`;
        return (
          <Link
            key={char.id}
            to={route}
            style={{ textDecoration: 'none' }}
            onClick={() => onCharacterClick?.(char)}
          >
            <CharacterCard
              ref={(r) => {
                const index = charactersWithVideo.findIndex(c => c.id === char.id);
                if (index !== -1) cardRefs.current[index] = r ?? null;
              }}
              character={char}
              activeCardId={activeCardId}
              setActiveCardId={setActiveCardId}
            />
          </Link>
        );
      })}
    </main>
  );
};

export default CharacterGrid; 